import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { motion } from 'framer-motion'
import { Upload, Image, Download, X, FileText } from 'lucide-react'
import { PDFDocument, rgb } from 'pdf-lib'
import toast from 'react-hot-toast'
import AdContainer from '../components/ads/AdContainer'
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig'

interface ConversionOptions {
  pageSize: 'A4' | 'Letter' | 'Legal' | 'A3' | 'A5'
  orientation: 'portrait' | 'landscape'
  margin: number
  quality: number
  fitToPage: boolean
}

interface ProcessedFile {
  id: string
  originalFile: File
  name: string
  size: number
  status: 'pending' | 'processing' | 'completed' | 'error'
  progress: number
  pdfBlob?: Blob
}

const JpgToPdf: React.FC = () => {
  const [files, setFiles] = useState<ProcessedFile[]>([])
  const [options, setOptions] = useState<ConversionOptions>({
    pageSize: 'A4',
    orientation: 'portrait',
    margin: 20,
    quality: 85,
    fitToPage: true
  })
  const [isProcessing, setIsProcessing] = useState(false)

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const imageFiles = acceptedFiles.filter(file => 
      file.type.startsWith('image/jpeg') || file.type.startsWith('image/jpg')
    )

    if (imageFiles.length === 0) {
      toast.error('Please select valid JPG/JPEG files')
      return
    }

    const newFiles: ProcessedFile[] = imageFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      originalFile: file,
      name: file.name,
      size: file.size,
      status: 'pending',
      progress: 0
    }))

    setFiles(prev => [...prev, ...newFiles])
    toast.success(`Added ${imageFiles.length} JPG file(s)`)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg']
    },
    multiple: true
  })

  const processFiles = async () => {
    if (files.length === 0) return

    setIsProcessing(true)
    
    for (const file of files.filter(f => f.status === 'pending')) {
      try {
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'processing', progress: 20 } : f
        ))

        // Create PDF document
        const pdfDoc = await PDFDocument.create()
        
        // Read the image file
        const imageBytes = await file.originalFile.arrayBuffer()
        let image
        
        try {
          // Try JPEG first
          image = await pdfDoc.embedJpg(imageBytes)
        } catch {
          try {
            // Fallback to PNG
            image = await pdfDoc.embedPng(imageBytes)
          } catch (error) {
            throw new Error('Unsupported image format')
          }
        }

        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 60 } : f
        ))

        // Calculate page dimensions
        let pageWidth, pageHeight
        switch (options.pageSize) {
          case 'A4':
            pageWidth = 595
            pageHeight = 842
            break
          case 'Letter':
            pageWidth = 612
            pageHeight = 792
            break
          case 'Legal':
            pageWidth = 612
            pageHeight = 1008
            break
          case 'A3':
            pageWidth = 842
            pageHeight = 1191
            break
          case 'A5':
            pageWidth = 420
            pageHeight = 595
            break
          default:
            pageWidth = 595
            pageHeight = 842
        }

        if (options.orientation === 'landscape') {
          [pageWidth, pageHeight] = [pageHeight, pageWidth]
        }

        // Create page
        const page = pdfDoc.addPage([pageWidth, pageHeight])
        
        // Calculate image dimensions with margins
        const margin = options.margin
        const availableWidth = pageWidth - (2 * margin)
        const availableHeight = pageHeight - (2 * margin)
        
        let imageWidth = image.width
        let imageHeight = image.height
        
        if (options.fitToPage) {
          const scaleX = availableWidth / image.width
          const scaleY = availableHeight / image.height
          const scale = Math.min(scaleX, scaleY)
          
          imageWidth = image.width * scale
          imageHeight = image.height * scale
        } else {
          // If image is larger than available space, scale it down
          if (imageWidth > availableWidth || imageHeight > availableHeight) {
            const scaleX = availableWidth / imageWidth
            const scaleY = availableHeight / imageHeight
            const scale = Math.min(scaleX, scaleY)
            
            imageWidth *= scale
            imageHeight *= scale
          }
        }

        // Center the image
        const x = (pageWidth - imageWidth) / 2
        const y = (pageHeight - imageHeight) / 2

        // Draw the image
        page.drawImage(image, {
          x,
          y,
          width: imageWidth,
          height: imageHeight,
        })

        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 90 } : f
        ))

        // Save PDF
        const pdfBytes = await pdfDoc.save()
        const blob = new Blob([pdfBytes], { type: 'application/pdf' })

        setFiles(prev => prev.map(f => 
          f.id === file.id 
            ? { ...f, status: 'completed', progress: 100, pdfBlob: blob }
            : f
        ))

      } catch (error) {
        console.error('Conversion error:', error)
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'error', progress: 0 } : f
        ))
        toast.error(`Failed to convert ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`)
      }
    }

    setIsProcessing(false)
    toast.success('JPG to PDF conversion completed!')
  }

  const downloadFile = (file: ProcessedFile) => {
    if (!file.pdfBlob) return

    const url = URL.createObjectURL(file.pdfBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${file.name.replace(/\.[^/.]+$/, '')}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-2xl mb-6">
            <FileText className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#8438ff] mb-4">
            JPG to PDF Converter
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Convert JPG images to PDF documents with customizable page settings and professional quality output.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.jpgToPdf?.header || '1234567890'}
          className="mb-8"
        />

        {/* Conversion Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card mb-8"
        >
          <div className="flex items-center space-x-2 mb-6">
            <FileText className="h-5 w-5 text-[#8438ff]" />
            <h2 className="text-lg font-semibold text-[#8438ff]">PDF Settings</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Page Size
              </label>
              <select
                value={options.pageSize}
                onChange={(e) => setOptions(prev => ({ ...prev, pageSize: e.target.value as any }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
              >
                <option value="A4">A4 (210 × 297 mm)</option>
                <option value="Letter">Letter (8.5 × 11 in)</option>
                <option value="Legal">Legal (8.5 × 14 in)</option>
                <option value="A3">A3 (297 × 420 mm)</option>
                <option value="A5">A5 (148 × 210 mm)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Orientation
              </label>
              <select
                value={options.orientation}
                onChange={(e) => setOptions(prev => ({ ...prev, orientation: e.target.value as any }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
              >
                <option value="portrait">Portrait</option>
                <option value="landscape">Landscape</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quality: {options.quality}%
              </label>
              <input
                type="range"
                min="50"
                max="100"
                value={options.quality}
                onChange={(e) => setOptions(prev => ({ ...prev, quality: parseInt(e.target.value) }))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                style={{
                  background: `linear-gradient(to right, #8438ff 0%, #8438ff ${((options.quality - 50) / (100 - 50)) * 100}%, #e5e7eb ${((options.quality - 50) / (100 - 50)) * 100}%, #e5e7eb 100%)`
                }}
              />
            </div>
          </div>

          <div className="mt-6 flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="fitToPage"
                checked={options.fitToPage}
                onChange={(e) => setOptions(prev => ({ ...prev, fitToPage: e.target.checked }))}
                className="w-4 h-4 rounded"
              />
              <label htmlFor="fitToPage" className="text-sm font-medium text-gray-700">
                Fit image to page
              </label>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mr-2">
                Margin: {options.margin}px
              </label>
              <input
                type="range"
                min="0"
                max="50"
                value={options.margin}
                onChange={(e) => setOptions(prev => ({ ...prev, margin: parseInt(e.target.value) }))}
                className="w-24 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                style={{
                  background: `linear-gradient(to right, #8438ff 0%, #8438ff ${(options.margin / 50) * 100}%, #e5e7eb ${(options.margin / 50) * 100}%, #e5e7eb 100%)`
                }}
              />
            </div>
          </div>
        </motion.div>

        {/* Upload Zone */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={`upload-zone ${isDragActive ? 'active' : ''} mb-8`}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-[#8438ff] mb-2">
            {isDragActive ? 'Drop your JPG files here' : 'Drag & drop JPG files here'}
          </p>
          <p className="text-gray-600 mb-4">
            or <span className="text-[#8438ff] font-medium">browse files</span>
          </p>
          <p className="text-sm text-gray-500">
            Supports JPG, JPEG • Max 100MB per file
          </p>
        </motion.div>

        {/* Content Ad - appears when files are uploaded */}
        {files.length > 0 && (
          <AdContainer 
            position="content" 
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.jpgToPdf?.content || '0987654321'}
            className="mb-8"
          />
        )}

        {/* File List */}
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-[#8438ff]">
                Files ({files.length})
              </h3>
              <button
                onClick={processFiles}
                disabled={isProcessing || !files.some(f => f.status === 'pending')}
                className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Converting...' : 'Convert to PDF'}
              </button>
            </div>

            <div className="space-y-4">
              {files.map((file) => (
                <div key={file.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <Image className="h-5 w-5 text-gray-500" />
                      <div>
                        <p className="font-medium text-gray-900">{file.name}</p>
                        <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {file.status === 'completed' && (
                        <button
                          onClick={() => downloadFile(file)}
                          className="btn btn-secondary text-sm"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download PDF
                        </button>
                      )}
                      <button
                        onClick={() => removeFile(file.id)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        disabled={file.status === 'processing'}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  {file.status !== 'pending' && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className={`font-medium ${
                          file.status === 'completed' ? 'text-green-600' : 
                          file.status === 'error' ? 'text-red-600' : 'text-blue-600'
                        }`}>
                          {file.status === 'processing' ? 'Converting...' : 
                           file.status === 'completed' ? 'Conversion completed' : 
                           file.status === 'error' ? 'Conversion failed' : ''}
                        </span>
                        <span className="text-gray-500">{file.progress}%</span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ width: `${file.progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.jpgToPdf?.footer || '1122334455'}
          className="mt-12"
        />
      </div>
    </div>
  )
}

export default JpgToPdf
